import { useEffect } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useGetUserSession } from '@/shared/hooks/api/auth'
import {
  ORGANIZATION_LLM_GATEWAY_PROVIDERS,
  useCurrentOrganization,
  useUpdateOrganization,
} from '@/shared/hooks/api/organization'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

const llmSettingsSchema = z.object({
  llmGatewayProvider: z.enum(ORGANIZATION_LLM_GATEWAY_PROVIDERS),
})

type LlmSettingsFormValues = z.infer<typeof llmSettingsSchema>

export function LlmSettingsForm() {
  const { t } = useTranslation('common')
  const { data: organization, isLoading: isOrganizationLoading } =
    useCurrentOrganization()
  const { data: session } = useGetUserSession()
  const { mutateAsync: updateOrganization, isPending: isUpdating } =
    useUpdateOrganization()
  const canEditOrgLlmGatewayProvider = session?.isArtemAdmin === true

  const form = useForm<LlmSettingsFormValues>({
    resolver: zodResolver(llmSettingsSchema),
    defaultValues: { llmGatewayProvider: 'openai' },
  })

  useEffect(() => {
    if (!organization) {
      return
    }
    form.reset({ llmGatewayProvider: organization.llmGatewayProvider })
  }, [organization, form])

  async function onSubmit(data: LlmSettingsFormValues) {
    if (!organization || !canEditOrgLlmGatewayProvider) {
      return
    }

    await updateOrganization({
      organizationId: organization.id,
      payload: { llmGatewayProvider: data.llmGatewayProvider },
    })

    toast.success(t('settingsLlm.updatedSuccess'))
  }

  const isDisabled = isOrganizationLoading || isUpdating || !organization

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
        <FormField
          control={form.control}
          name='llmGatewayProvider'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('settingsLlm.providerLabel')}</FormLabel>
              <Select
                value={field.value}
                onValueChange={(value) => {
                  if (value) {
                    field.onChange(value)
                  }
                }}
                disabled={isDisabled || !canEditOrgLlmGatewayProvider}
                items={[
                  {
                    value: 'openai',
                    label: t('settingsLlm.providerOpenAi'),
                  },
                  {
                    value: 'qwen',
                    label: t('settingsLlm.providerQwen'),
                  },
                  {
                    value: 'bedrock',
                    label: t('settingsLlm.providerBedrock'),
                  },
                ]}
              >
                <FormControl>
                  <SelectTrigger id='settings-llm-gateway-provider'>
                    <SelectValue
                      placeholder={t('settingsLlm.providerPlaceholder')}
                    />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value='openai'>
                    {t('settingsLlm.providerOpenAi')}
                  </SelectItem>
                  <SelectItem value='qwen'>
                    {t('settingsLlm.providerQwen')}
                  </SelectItem>
                  <SelectItem value='bedrock'>
                    {t('settingsLlm.providerBedrock')}
                  </SelectItem>
                </SelectContent>
              </Select>
              <FormDescription>
                {t('settingsLlm.providerDescription')}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          type='submit'
          disabled={isDisabled || !canEditOrgLlmGatewayProvider}
        >
          {t('settingsLlm.updateButton')}
        </Button>
      </form>
    </Form>
  )
}
